import i18n from "i18n";
import React, { useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { AxiosError, AxiosInstance } from "axios";
import { useMutation, useQueryClient } from "react-query";

import { Project } from "types";
import { useClient } from "utils/client";
import { Risk, RiskGroupping } from "./types";
import { Modal } from "components/Modal/Modal";
import RadioInput from "components/inputs/RadioInput";
import SelectInput from "components/inputs/SelectInput";
import { useRiskTemplates } from "pages/Risk/hooks/useRisks";
import { useRiskTemplates as useCustomRiskTemplates } from "components/RiskTemplates/hooks/useRiskTemplates";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import SearchSelectInput from "components/inputs/SearchSelectInput";
import { templatesToDisplay } from "components/RiskTemplates/CreateRiskTemplateOnDashboard";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";

interface CreateProjectOnDashboardProps {
  projectCRN: string;
  defaultGroupId?: string;
  button: React.FunctionComponent<React.ButtonHTMLAttributes<any>>;
}

export const CreateRiskOnDashboard: React.FunctionComponent<
  CreateProjectOnDashboardProps
> = (props) => {
  const [modalVisible, setModalVisible] = React.useState(false);

  return (
    <>
      <div>
        <props.button onClick={() => setModalVisible(true)} />
      </div>

      <>
        <Modal
          onClose={() => setModalVisible(false)}
          show={modalVisible}
          width="1/2"
          content={
            <CreateRiskForm
              projectCRN={props.projectCRN}
              defaultGroupId={props.defaultGroupId}
            />
          }
        />
      </>
    </>
  );
};

enum CreateOption {
  customTemplate = "customTemplate",
  template = "template",
  scratch = "scratch",
}

const createRisk = async (
  client: AxiosInstance,
  { risk, mitigations }: { risk?: Risk; mitigations: any },
  projectId: string
) => {
  try {
    const riskPayload = risk
      ? {
          ...risk,
          assessment: {
            impact: risk.impact,
            people_count: risk.people_count,
            duration: risk.duration,
            probability: risk.probability,
            detectability: risk.detectability,
          },
          mitigations: mitigations,
        }
      : { mitigations: null };

    const { data } = await client.post<Risk>(
      `/projects/${projectId}/risks`,
      riskPayload
    );

    return data;
  } catch (error) {
    console.error(error);
    throw new Error((error as AxiosError).response?.data);
  }
};

export const useCreateRisk = (projectCRN: string) => {
  const client = useClient();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { project } = useProjectDetails(projectCRN);

  const { mutate, isLoading } = useMutation(
    async ({
      template,
      grouping,
    }: {
      template?: Risk;
      grouping?: RiskGroupping;
    }) => {
      const data = await createRisk(
        client,
        {
          risk: template,
          // @ts-ignore
          mitigations: template?.mitigations || [],
        },
        projectCRN
      );

      if (grouping && project) {
        const newGrouping = [...(grouping?.members || []), data.resource_id];

        project.riskGrouping.find((g) => g.id === grouping.id)!.members =
          newGrouping;

        const payload: Project = {
          ...project,
          risks: [...(project.risks || []), data],
        };
        await client.put(`/projects/${project.projectdata.crn}`, {
          payload,
        });
      }

      return data;
    },
    {
      onSuccess: async (risk) => {
        await queryClient.refetchQueries(["projects", projectCRN]);
        history.push(`/projects/${projectCRN}/risks/${risk.resource_id}/edit`);
      },
    }
  );

  return { createRisk: mutate, isLoading };
};

interface CreateRiskFormProps {
  projectCRN: string;
  defaultGroupId?: string;
}

const CreateRiskForm = (props: CreateRiskFormProps) => {
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  const { risks: templates } = useRiskTemplates();
  const { project } = useProjectDetails(props.projectCRN);
  const { riskTemplates: customTemplates } = useCustomRiskTemplates();
  const language = i18n.language.split("-")[0].toUpperCase();
  const { createRisk, isLoading } = useCreateRisk(props.projectCRN);
  const [selectedOption, setSelectedOption] = useState<CreateOption>(
    CreateOption.scratch
  );

  const [selectedTemplate, setSelectedTemplate] = useState<Risk>();
  const [selectedGrouping, setSelectedGrouping] = useState<
    RiskGroupping | undefined
  >(project?.riskGrouping?.find((g) => g.id === props.defaultGroupId));

  const handleCreateRisk = () => {
    createRisk({ template: selectedTemplate, grouping: selectedGrouping });
  };

  const langRegex = new RegExp(`\\[${language}\\]`, "g");

  const options = [
    {
      label: t(`risks.create.options.${CreateOption.scratch}`),
      value: CreateOption.scratch,
    },
    {
      label: t(`risks.create.options.${CreateOption.template}`),
      value: CreateOption.template,
    },
    isFeatureEnabled(Feature.RiskTemplates)
      ? {
          label: t(`risks.create.options.${CreateOption.customTemplate}`),
          value: CreateOption.customTemplate,
        }
      : null,
  ].filter((o) => !!o);

  return (
    <div>
      <span className="text-xl">{t("risks.create.headline")}</span>
      <div className="pt-4 pb-4">
        <RadioInput
          islast
          required
          id="useTemplate"
          name="useTemplate"
          label={t("risks.create.create_choice")}
          defaultValue={selectedOption}
          onBlur={(option) =>
            setSelectedOption(option.target.value as CreateOption.scratch)
          }
          options={options}
        />

        {selectedOption === CreateOption.template && (
          <SearchSelectInput
            required
            id="template"
            name="template"
            defaultValue={""}
            label={t("risks.create.template")}
            onBlur={(option) =>
              setSelectedTemplate(
                templates!.find((t) => t.resource_id === option.target.value)!
              )
            }
            options={
              templatesToDisplay(
                templates
                  ?.filter((template) =>
                    template.blueprint_name?.match(langRegex)
                  )
                  ?.map((template) => ({
                    value: template.resource_id,
                    label: `${t(
                      "form.risk.options.group." + template?.group_id,
                      {
                        defaultValue: t("riskTemplate.selection.noGroup"),
                      }
                    )} - ${template.blueprint_name!.replace(langRegex, "")}`,
                  }))
              ) || []
            }
          />
        )}
        {selectedOption === CreateOption.customTemplate && (
          <SearchSelectInput
            required
            id="template"
            name="template"
            label={t("risks.create.template")}
            defaultValue={
              selectedTemplate &&
              `${t("form.risk.options.group." + selectedTemplate?.group_id, {
                defaultValue: t("riskTemplate.selection.noGroup"),
              })} - ${selectedTemplate?.blueprint_name!}`
            }
            onBlur={(option) =>
              setSelectedTemplate(
                customTemplates!
                  .map((t) => t.payload)
                  .find((t) => t.resource_id === option.target.value)!
              )
            }
            options={
              templatesToDisplay(
                customTemplates
                  .map((t) => t.payload)
                  ?.map((template) => ({
                    value: template.resource_id,
                    label: `${t(
                      "form.risk.options.group." + template.group_id,
                      {
                        defaultValue: t("riskTemplate.selection.noGroup"),
                      }
                    )} - ${template.blueprint_name!}`,
                  }))
              ) || []
            }
          />
        )}

        <div className="pt-2">
          {project?.riskGrouping && (
            <SearchSelectInput
              id="template"
              name="riskGroup"
              label={t("risks.create.chapter")}
              defaultValue={selectedGrouping?.label}
              onBlur={(option) =>
                setSelectedGrouping(
                  project?.riskGrouping.find(
                    (g) => g.id === option.target.value
                  )!
                )
              }
              options={project?.riskGrouping?.map((group) => ({
                value: group.id,
                label: group.label,
              }))}
            />
          )}
        </div>
      </div>

      <PrimaryButton
        float="right"
        disabled={
          ([CreateOption.template, CreateOption.customTemplate].includes(
            selectedOption
          ) &&
            !selectedTemplate) ||
          isLoading
        }
        onClick={handleCreateRisk}
      >
        {isLoading ? t("risks.create.cta_loading") : t("risks.create.cta")}
      </PrimaryButton>
    </div>
  );
};
